import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Home, ChevronRight, LayoutGrid, List } from 'lucide-react'
import { getProjects, getDocuments, getFolders } from '../../services/api'
import FolderTree from '../../components/documents/FolderTree'
import DocumentCard from '../../components/documents/DocumentCard'
import DocumentGrid from '../../components/documents/DocumentGrid'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import EmptyState from '../../components/ui/EmptyState'
import PageHeader from '../../components/ui/PageHeader'
import ContextualTip from '../../components/ui/ContextualTip'
import HelpPopover from '../../components/help/HelpPopover'
import { FileText } from 'lucide-react'

export default function SharedProjectView() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()

  const [docView, setDocView] = useState<'list' | 'grid'>(() => {
    try { return (localStorage.getItem('innovateflow:doc-view:shared') as 'list' | 'grid') || 'list' } catch { return 'list' }
  })

  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => getProjects(),
  })
  const project = projects?.find((p) => p.project_id === projectId)

  const { data: folders, isLoading: foldersLoading } = useQuery({
    queryKey: ['shared-folders', projectId],
    queryFn: () => getFolders(projectId!),
    enabled: !!projectId,
  })

  const { data: documents, isLoading: docsLoading } = useQuery({
    queryKey: ['shared-documents', projectId],
    queryFn: () => getDocuments(projectId!),
    enabled: !!projectId,
  })

  if (foldersLoading || docsLoading) return <LoadingSkeleton rows={4} />

  return (
    <div>
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
        <button onClick={() => navigate('/shared')} className="hover:text-iflow-600 flex items-center gap-1">
          <Home size={14} /> Shared Projects
        </button>
        <ChevronRight size={14} />
        <span className="text-gray-800 font-medium">{project?.name || 'Project'}</span>
      </div>

      <PageHeader
        title={project?.name || 'Shared Project'}
        description="A project shared with you. You can open only what your granted disclosure level authorises."
        helpKey="shared"
        status={{ label: 'Shared with you', tone: 'info' }}
      />

      <ContextualTip id="shared-project-view-ladder" tone="info">
        You are viewing this project at the disclosure level you were granted.
        Some documents or detail may be redacted until you are moved up the
        ladder — the L3→L4 step exchanges identity and requires a signed NDA,
        and full collaboration is the top rung (L6).{' '}
        <HelpPopover topic="concept-disclosure-ladder" size={14} />
      </ContextualTip>

      <div className="mt-4 flex gap-6">
        <div className="hidden lg:block w-64 flex-shrink-0">
          <div className="light-card p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Folders</h3>
            <FolderTree folders={folders || []} projectId={projectId!} currentFolderId={null} isShared />
          </div>
        </div>

        <div className="flex-1">
          {!documents?.length ? (
            <EmptyState
              icon={FileText}
              title="No documents to show"
              description="This shared project has no documents at your disclosure level yet. If you expected files here, ask the owner to raise your access."
            />
          ) : (
            <>
              <div className="flex justify-end mb-3">
                <div className="flex items-center border border-sky-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => { setDocView('list'); try { localStorage.setItem('innovateflow:doc-view:shared', 'list') } catch {} }}
                    className={`p-1.5 ${docView === 'list' ? 'bg-iflow-50 text-iflow-600' : 'text-gray-500 hover:text-gray-600'}`}
                    title="List view" aria-pressed={docView === 'list'}
                  ><List size={14} /></button>
                  <button
                    onClick={() => { setDocView('grid'); try { localStorage.setItem('innovateflow:doc-view:shared', 'grid') } catch {} }}
                    className={`p-1.5 ${docView === 'grid' ? 'bg-iflow-50 text-iflow-600' : 'text-gray-500 hover:text-gray-600'}`}
                    title="Grid view" aria-pressed={docView === 'grid'}
                  ><LayoutGrid size={14} /></button>
                </div>
              </div>
              {docView === 'grid' ? (
                <DocumentGrid documents={documents} linkPrefix="/shared" />
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <DocumentCard key={doc.document_id} document={doc} linkPrefix="/shared" />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
